import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  unique
} from "drizzle-orm/pg-core";
import { claims } from './claims';
import { importGroupActionEnum, importGroupMatchStatusEnum, importItemStatusEnum, importJobStatusEnum } from './enums';
import { leads } from './leads';
import { organizations } from './organizations';
import { users } from './users';

export const importJobs = pgTable("import_jobs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  fileName: varchar("file_name").notNull(),
  status: importJobStatusEnum("status").notNull().default("parsing"),
  totalItems: integer("total_items").default(0),
  totalGroups: integer("total_groups").default(0),
  progress: jsonb("progress").default({ downloaded: 0, processed: 0, failed: 0, matched: 0 }),
  csvHeaders: jsonb("csv_headers"),
  error: text("error"),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
}, (table) => [
  index("idx_import_jobs_user_id").on(table.userId),
  index("idx_import_jobs_status").on(table.status),
]);

export const importJobItems = pgTable("import_job_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: uuid("job_id").references(() => importJobs.id, { onDelete: "cascade" }).notNull(),
  sourceId: varchar("source_id").notNull(),
  rowIndex: integer("row_index").notNull(),
  subjectId: varchar("subject_id").notNull(),
  subjectType: varchar("subject_type"),
  downloadUrl: text("download_url").notNull(),
  originalName: varchar("original_name"),
  mimeType: varchar("mime_type"),
  fileSizeBytes: integer("file_size_bytes"),
  metadata: jsonb("metadata"),

  // Download + Processing status
  status: importItemStatusEnum("status").notNull().default("pending"),
  error: text("error"),

  // Storage after download
  storedFilePath: text("stored_file_path"),
  fileHash: varchar("file_hash"),

  // OCR & AI analysis results
  ocrText: text("ocr_text"),
  ocrConfidence: integer("ocr_confidence"),
  aiAnalysis: jsonb("ai_analysis"),

  // Deduplication
  isDuplicate: boolean("is_duplicate").default(false),
  duplicateOf: jsonb("duplicate_of"),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_import_job_items_job_id").on(table.jobId),
  index("idx_import_job_items_subject").on(table.jobId, table.subjectId),
  index("idx_import_job_items_status").on(table.jobId, table.status),
  index("idx_import_job_items_file_hash").on(table.fileHash),
  unique("uq_import_job_items_source").on(table.jobId, table.sourceId),
]);

export const importJobGroups = pgTable("import_job_groups", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: uuid("job_id").references(() => importJobs.id, { onDelete: "cascade" }).notNull(),
  subjectId: varchar("subject_id").notNull(),
  subjectType: varchar("subject_type"),
  itemCount: integer("item_count").default(0),

  // AI matching results
  matchStatus: importGroupMatchStatusEnum("match_status").notNull().default("pending"),
  matchedClaimId: uuid("matched_claim_id").references(() => claims.id),
  matchedLeadId: uuid("matched_lead_id").references(() => leads.id),
  matchConfidence: integer("match_confidence"),
  matchReasoning: text("match_reasoning"),
  candidateMatches: jsonb("candidate_matches"),
  extractedIdentifiers: jsonb("extracted_identifiers"),

  // User action
  action: importGroupActionEnum("action").notNull().default("pending"),
  actionTargetId: uuid("action_target_id"),
  actionByUserId: varchar("action_by_user_id").references(() => users.id),
  actionAt: timestamp("action_at"),
  importedCount: integer("imported_count").default(0),
  skippedDuplicates: integer("skipped_duplicates").default(0),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_import_job_groups_job_id").on(table.jobId),
  index("idx_import_job_groups_match_status").on(table.jobId, table.matchStatus),
  index("idx_import_job_groups_action").on(table.jobId, table.action),
]);
